import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { PhoneSyncApi, useQueryDevices } from '@frontend/api-phone-sync';
import { useAppSettingsShallowStore } from '@frontend/app-settings-store';
import { useTranslation } from '@frontend/i18n';
import { useLocationPicker } from '@frontend/location-picker';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import {
  DeviceSelectModal,
  LosingAccessModal,
  PhoneSyncButton,
  PhoneSyncModal,
  usePhonePairSuccessHandler,
  usePhoneSyncControl,
  usePhoneSyncMutations,
} from '@frontend/phone-sync';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { useModalControl } from '@frontend/design-system';
import { SoftphoneCallActiveError, SoftphoneIsConnectedTooltip, usePhoneConfigInfo } from '../shared';

type UsePrivilegedPhoneSoftphoneIntegrationProps = {
  handleOrganizationChange: () => void;
  closeModal?: () => void;
};

export const ERRORS = {
  SOFTPHONE_CALL_ACTIVE: 'softphone call active',
  OPENING_DEVICE_SELECT_MODAL: 'opening device select modal',
};

export const usePrivilegedPhoneSoftphoneIntegration = ({
  handleOrganizationChange,
  closeModal,
}: UsePrivilegedPhoneSoftphoneIntegrationProps) => {
  const { t } = useTranslation('location-picker');
  const { selectedLocationIds, accessibleLocationData, accessibleGroupData } = useAppScopeStore();

  const { inFlightLocationIds, inFlightOrganizationId, setInFlightOrganizationId, setInFlightLocationIds } =
    useLocationPicker([
      'inFlightLocationIds',
      'inFlightOrganizationId',
      'setInFlightOrganizationId',
      'setInFlightLocationIds',
    ]);

  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const {
    data: devices,
    refetch: refetchDevices,
    error: _devicesError,
  } = useQueryDevices(inFlightLocationIds, {
    useErrorBoundary: false,
  });
  const deviceSelectModal = useModalControl();
  const losingAccessModal = useModalControl();

  const hasAvailableDevices = devices && devices.filter((device) => device.availableInContext).length > 0;

  // Softphone specific state
  const [showActivePhoneCallError, setShowActivePhoneCallError] = useState(false);
  const { hasActiveSoftphoneCalls } = useAppSettingsShallowStore('hasActiveSoftphoneCalls');

  // Phone mutations and controls
  const { disconnectDevice } = usePhoneSyncMutations();
  const { modalProps, buttonProps } = usePhoneSyncControl();
  const onSyncSuccess = usePhonePairSuccessHandler()(inFlightLocationIds);
  const phoneConfigInfo = usePhoneConfigInfo({
    phoneConfig,
    accessibleLocationData: { ...accessibleLocationData, ...accessibleGroupData },
  });

  // In the case where a softphone call is active & the user tries to update the filters to location(s) and org that is not associated w/ the softphone,
  // update the inFlight locations & orgId to include the softphone orgId & locationId
  const handleSoftphoneActiveRevertFilters = useCallback(() => {
    if (!phoneConfigInfo || !phoneConfigInfo?.isSoftphone) return;
    if (inFlightOrganizationId !== phoneConfigInfo.orgId) {
      setInFlightOrganizationId(phoneConfigInfo.orgId);
    }

    setInFlightLocationIds(selectedLocationIds);
  }, [inFlightOrganizationId, phoneConfigInfo, selectedLocationIds, setInFlightLocationIds, setInFlightOrganizationId]);

  // Get whether or not we can update the location filters when a softphone call is active.
  const getCanUpdateWhenSoftphoneActive = useCallback(() => {
    if (!hasActiveSoftphoneCalls || !phoneConfigInfo?.isSoftphone) return true;

    const softphoneOrgIdInFlight = inFlightOrganizationId === phoneConfigInfo.orgId;
    const softphoneLocationIdInFlight = inFlightLocationIds.includes(phoneConfigInfo.locationId);

    // We can update if the orgId & locationId associated w/ the softphone are 'in flight'
    return softphoneOrgIdInFlight && softphoneLocationIdInFlight;
  }, [hasActiveSoftphoneCalls, phoneConfigInfo, inFlightOrganizationId, inFlightLocationIds]);

  const handleViewLocation = useCallback(async () => {
    if (!getCanUpdateWhenSoftphoneActive()) {
      setShowActivePhoneCallError(true);
      handleSoftphoneActiveRevertFilters();
      return;
    }

    // Get the new list of devices that are available within the selected location ids
    const { data: devices } = await refetchDevices();

    // These are devices that are currently in context (available within the selected location ids)
    const availableDevices = devices?.filter((device) => device.availableInContext);
    const isCurrentDeviceWithinContext =
      phoneConfig && availableDevices?.some((device) => phoneConfig.sipProfileId === device.sipProfileId);

    const movingAwayFromCurrentConfig = phoneConfig && !isCurrentDeviceWithinContext;
    const hasAvailableDevices = availableDevices && availableDevices.length;
    // The SelectDeviceModal should open if the location with the connected device is deselected (or if no device is connected),
    // and the newly selected location has available devices to be connected.
    const shouldPromptForDeviceSelection = movingAwayFromCurrentConfig;

    if (shouldPromptForDeviceSelection) {
      if (hasAvailableDevices) {
        deviceSelectModal.openModal();
      } else {
        // No available devices to connect to - warn the user
        losingAccessModal.openModal();
      }
      return Promise.reject(ERRORS.OPENING_DEVICE_SELECT_MODAL);
    } else {
      handleOrganizationChange();
      closeModal?.();
    }
  }, [
    getCanUpdateWhenSoftphoneActive,
    setShowActivePhoneCallError,
    handleSoftphoneActiveRevertFilters,
    refetchDevices,
    phoneConfig,
    deviceSelectModal,
    losingAccessModal,
    handleOrganizationChange,
    closeModal,
  ]);

  const renderOrgInfo = useCallback(
    (orgId: string) => {
      if (phoneConfigInfo?.orgId === orgId) {
        return <SoftphoneIsConnectedTooltip deviceName={phoneConfig?.name} />;
      }

      return null;
    },
    [phoneConfigInfo, phoneConfig]
  );

  const renderLocationInfo = useCallback(
    (locationId: string) => {
      if (phoneConfigInfo?.locationId === locationId) {
        return <SoftphoneIsConnectedTooltip deviceName={phoneConfig?.name} />;
      }

      return null;
    },
    [phoneConfigInfo, phoneConfig]
  );

  const onApplyError = useCallback((error: unknown) => {
    if (error === ERRORS.SOFTPHONE_CALL_ACTIVE) {
      return;
    }

    if (error === ERRORS.OPENING_DEVICE_SELECT_MODAL) {
      return;
    }

    console.error(error);
  }, []);

  const onClickOrgRadio = useCallback(() => {
    setShowActivePhoneCallError(false);
  }, []);

  const renderBannerInfo = useCallback(() => {
    if (showActivePhoneCallError && phoneConfigInfo) {
      return (
        <div
          css={css`
            padding: ${theme.spacing(2, 2, 1, 2)};
            border-top: 1px solid ${theme.colors.neutral20};
          `}
        >
          <SoftphoneCallActiveError locationName={accessibleLocationData[phoneConfigInfo.locationId]?.name || ''} />
        </div>
      );
    }

    return null;
  }, [showActivePhoneCallError, phoneConfigInfo, accessibleLocationData]);

  const renderPhoneRelatedModals = useCallback(() => {
    return (
      <>
        {hasAvailableDevices ? (
          <>
            <DeviceSelectModal
              variant='location-picker'
              locationIds={inFlightLocationIds}
              deviceSelectModal={deviceSelectModal}
              closeModal={() => {
                handleOrganizationChange();
                closeModal?.();
              }}
              pairDeviceButton={
                <PhoneSyncButton
                  {...buttonProps}
                  variant='tertiary'
                  onClick={() => {
                    deviceSelectModal.closeModal();
                  }}
                />
              }
            />
            <PhoneSyncModal
              {...modalProps}
              locationIds={inFlightLocationIds}
              onBack={() => {
                deviceSelectModal.openModal();
                modalProps.onClose();
              }}
              onSyncSuccess={(config) => {
                onSyncSuccess(config);

                handleOrganizationChange();
                closeModal?.();
              }}
              syncFn={PhoneSyncApi.pairDevice}
            />
          </>
        ) : (
          <>
            <LosingAccessModal
              modal={losingAccessModal}
              onContinue={() => {
                handleOrganizationChange();
                disconnectDevice();
                modalProps.onClose();
                closeModal?.();
              }}
              pairDeviceButton={
                <PhoneSyncButton
                  {...buttonProps}
                  variant='secondary'
                  btnLabel={t('Pair New Device')}
                  onClick={() => {
                    losingAccessModal.closeModal();
                  }}
                />
              }
            />
            <PhoneSyncModal
              {...modalProps}
              locationIds={inFlightLocationIds}
              onBack={() => {
                modalProps.onClose();
              }}
              onSyncSuccess={(config) => {
                onSyncSuccess(config);
                handleOrganizationChange();
                closeModal?.();
              }}
              syncFn={PhoneSyncApi.pairDevice}
            />
          </>
        )}
      </>
    );
  }, [
    hasAvailableDevices,
    inFlightLocationIds,
    deviceSelectModal,
    handleOrganizationChange,
    closeModal,
    buttonProps,
    modalProps,
    onSyncSuccess,
    losingAccessModal,
    disconnectDevice,
    t,
  ]);

  return {
    hasAvailableDevices,
    showActivePhoneCallError,
    phoneConfigInfo,
    handleViewLocation,
    renderOrgInfo,
    renderLocationInfo,
    renderBannerInfo,
    onApplyError,
    onClickOrgRadio,
    renderPhoneRelatedModals,
  };
};
